import type { Breadcrumb, Event as SentryEvent } from '@sentry/react';

/**
 * Deep, cycle-safe scrubbing for Sentry payloads so no PII ever leaves the
 * browser. Exposed as pure functions (no Sentry side effects) so they can be
 * unit-tested directly.
 */

export const REDACTED = '[Filtered]';

/** Maximum recursion depth — guards against pathological nesting. */
const MAX_DEPTH = 8;

/**
 * Object keys whose VALUES must be fully redacted. Matched case-insensitively
 * as substrings, so `contact_email`, `id_front_image`, `access_token`, etc. are
 * all caught.
 */
const SENSITIVE_KEY_SUBSTRINGS = [
  'password',
  'token',
  'authorization',
  'cookie',
  'email',
  'recipient',
  'phone',
  'bride_name',
  'groom_name',
  'full_name',
  'guest_name',
  'id_number',
  'id_front',
  'id_back',
  'address',
];

/** Redact email addresses found anywhere inside a string value. */
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/** Redact the VALUE of any `*token=...` URL query parameter, keeping the key. */
const TOKEN_QS_RE = /((?:[\w-]*token)=)([^&\s#"']*)/gi;

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEY_SUBSTRINGS.some((substr) => lower.includes(substr));
}

/** Redact email addresses and token query-param values inside a string. */
export function scrubString(value: string): string {
  return value.replace(EMAIL_RE, REDACTED).replace(TOKEN_QS_RE, (_match, prefix) => `${prefix}${REDACTED}`);
}

function scrubValue(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return scrubString(value);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (depth >= MAX_DEPTH) {
    return value;
  }
  // Cycle guard: never descend into an object we are already visiting.
  if (seen.has(value as object)) {
    return value;
  }
  seen.add(value as object);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = scrubValue(value[i], depth + 1, seen);
    }
    return value;
  }

  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (isSensitiveKey(key)) {
      obj[key] = REDACTED;
    } else {
      obj[key] = scrubValue(obj[key], depth + 1, seen);
    }
  }
  return value;
}

/** Deep-scrub an arbitrary value, starting a fresh cycle guard per tree. */
function deepScrub(value: unknown): unknown {
  return scrubValue(value, 0, new WeakSet<object>());
}

/** Scrub `request.data`, which may be a plain object or a JSON string. */
function scrubRequestData(data: unknown): unknown {
  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data);
      return JSON.stringify(deepScrub(parsed));
    } catch {
      // Not JSON (e.g. form-encoded / raw) — scrub as a plain string.
      return scrubString(data);
    }
  }
  return deepScrub(data);
}

/**
 * Deep-scrub a Sentry event in place (also returned for convenience). Walks the
 * request payload, extra, contexts, user and breadcrumbs — every place PII can
 * realistically surface.
 */
export function scrubSentryEvent<T extends SentryEvent>(event: T): T {
  if (!event || typeof event !== 'object') {
    return event;
  }

  if (event.request) {
    const req = event.request;
    if (req.data !== undefined && req.data !== null) {
      req.data = scrubRequestData(req.data);
    }
    if (typeof req.query_string === 'string') {
      req.query_string = scrubString(req.query_string);
    } else if (req.query_string && typeof req.query_string === 'object') {
      req.query_string = deepScrub(req.query_string) as typeof req.query_string;
    }
    if (typeof req.url === 'string') {
      req.url = scrubString(req.url);
    }
    if (req.headers) {
      req.headers = deepScrub(req.headers) as typeof req.headers;
    }
  }

  if (event.extra) {
    event.extra = deepScrub(event.extra) as typeof event.extra;
  }
  if (event.contexts) {
    event.contexts = deepScrub(event.contexts) as typeof event.contexts;
  }
  if (event.user) {
    event.user = deepScrub(event.user) as typeof event.user;
  }

  if (Array.isArray(event.breadcrumbs)) {
    for (const crumb of event.breadcrumbs) {
      scrubBreadcrumb(crumb);
    }
  }

  return event;
}

/**
 * Redact email / token values from a breadcrumb's message and data. Returns the
 * (mutated) breadcrumb so it can be used directly as a `beforeBreadcrumb` hook.
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb | null): Breadcrumb | null {
  if (!breadcrumb) {
    return breadcrumb;
  }
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = scrubString(breadcrumb.message);
  }
  if (breadcrumb.data) {
    breadcrumb.data = deepScrub(breadcrumb.data) as typeof breadcrumb.data;
  }
  return breadcrumb;
}
